(() => {
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency,
    }).format(cents / 100);
  };

  const showToast = (message) => {
    const toast = qs("#Turn9Toast");
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add("is-visible");
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove("is-visible"), 2800);
  };

  const initMenu = () => {
    const toggle = qs("[data-menu-toggle]");
    const menu = qs("[data-mobile-menu]");
    if (!toggle || !menu) return;

    toggle.addEventListener("click", () => {
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!isOpen));
      menu.hidden = isOpen;
      document.body.classList.toggle("menu-open", !isOpen);
    });

    qsa("a", menu).forEach((link) => {
      link.addEventListener("click", () => {
        toggle.setAttribute("aria-expanded", "false");
        menu.hidden = true;
        document.body.classList.remove("menu-open");
      });
    });
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      qsa("[data-cart-count]").forEach((count) => {
        count.textContent = cart.item_count;
      });
    } catch (_error) {
      // A cart-count refresh should never interrupt shopping.
    }
  };

  const initProductForm = (section) => {
    const form = qs(".product-form", section);
    const variantData = qs("[data-product-json]", section);
    if (!form || !variantData) return;

    let variants = [];
    try {
      variants = JSON.parse(variantData.textContent);
    } catch (_error) {
      return;
    }

    const variantInput = qs("[data-variant-id]", form);
    const addButton = qs("[data-add-to-cart]", form);
    const addText = qs("[data-add-to-cart-text]", form);
    const price = qs("[data-product-price]", section);

    const selectedOptions = () =>
      qsa("[data-option-position]", form).map((fieldset) => {
        const selected = qs("input:checked", fieldset);
        return selected?.value;
      });

    const selectVariant = () => {
      const options = selectedOptions();
      const variant = variants.find((candidate) =>
        candidate.options.every((option, index) => option === options[index]),
      );

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addText) addText.textContent = "Unavailable";
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (addButton) addButton.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? "Add to bag" : "Sold out";

      if (price) {
        const sale =
          variant.compare_at_price && variant.compare_at_price > variant.price
            ? `<s class="price-compare">${formatMoney(variant.compare_at_price)}</s>`
            : "";
        price.innerHTML = `<span class="price-current">${formatMoney(variant.price)}</span>${sale}`;
      }

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);

      if (variant.featured_media) {
        const featuredMedia = qs(`[data-media-id="${variant.featured_media.id}"]`, section);
        featuredMedia?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    qsa("[data-option-input]", form).forEach((input) => {
      input.addEventListener("change", selectVariant);
    });

    qsa("[data-quantity-change]", form).forEach((button) => {
      button.addEventListener("click", () => {
        const input = qs('input[name="quantity"]', form);
        if (!input) return;
        const next = Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange));
        input.value = String(next);
      });
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!addButton || addButton.disabled) return;

      const originalText = addText?.textContent;
      addButton.disabled = true;
      if (addText) addText.textContent = "Adding…";

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || "Could not add this item.");
        await updateCartCount();
        if (addText) addText.textContent = "Added";
        showToast(`${result.product_title || "Item"} added to your bag.`);
      } catch (error) {
        if (addText) addText.textContent = "Try again";
        showToast(error.message || "Something went wrong. Please try again.");
      } finally {
        window.setTimeout(() => {
          addButton.disabled = false;
          if (addText) addText.textContent = originalText || "Add to bag";
        }, 1300);
      }
    });
  };

  const initDialogs = () => {
    qsa("[data-dialog-open]").forEach((button) => {
      const dialog = document.getElementById(button.dataset.dialogOpen);
      if (!dialog) return;
      button.addEventListener("click", () => dialog.showModal());
      qs("[data-dialog-close]", dialog)?.addEventListener("click", () => dialog.close());
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  };

  const initSort = () => {
    const form = qs("[data-sort-form]");
    if (!form) return;
    qs("select", form)?.addEventListener("change", (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", event.target.value);
      window.location.assign(url.toString());
    });
  };

  const initRecommendations = () => {
    qsa("product-recommendations").forEach(async (element) => {
      if (!element.dataset.url) return;
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, "text/html");
        const recommendations = qs("product-recommendations", html);
        if (recommendations?.innerHTML.trim()) element.innerHTML = recommendations.innerHTML;
      } catch (_error) {
        // Recommendations are optional and should fail silently.
      }
    });
  };

  const init = (root = document) => {
    qsa("[data-product-section]", root).forEach(initProductForm);
    initDialogs();
    initSort();
    initRecommendations();
  };

  document.documentElement.classList.remove("no-js");
  document.addEventListener("DOMContentLoaded", () => {
    initMenu();
    init();
  });

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
